import { ref } from 'vue'
import { getUserMe, postLogout, postRefreshToken } from '@/modules/auth/api'
import useUser from '@/modules/auth/composables/use-user'
import type { User } from '@/modules/auth/model'
import { isAxiosError } from 'axios'
import { clearLocalhostCookie } from '@/composables/cookies'
import useError from '@/composables/error/useError'

const { setUser } = useUser()

const error = ref<number>()
const authorized = ref(false)

const jsonExpectedError = 'JSON expected, HTML received'

const authorize = async (): Promise<boolean> => {
    try {
        error.value = undefined
        setUser(await getUserAction())
        authorized.value = true
        return true
    } catch (e) {
        // hack for CloudFront swallowing 403 and replacing with HTML
        if (e instanceof Error && e.message === jsonExpectedError) {
            error.value = 403
            return false
        }

        if (isAxiosError(e) && (e.response?.status === 401 || e.response?.status === 403)) {
            error.value = e.response.status
            return false // invalid cookie (401) or no cookie (403)
        }

        throw e // unhandled error
    }
}

const logOut = async () => {
    const response = await postLogout().call()
    clearLocalhostCookie('access_token')
    clearLocalhostCookie('refresh_token')
    localStorage.removeItem('tl-dashboard-workspace-id')
    return response
}

const getUserAction = async (): Promise<User> => {
    const response = await getUserMe().call()

    if (!response) {
        throw new Error('Unable to get User, no response')
    }
    if (!(response.headers['content-type'] as string | undefined)?.includes('json')) {
        throw new Error(jsonExpectedError)
    }

    return response.data
}

const refreshTokenAction = async () => {
    await postRefreshToken().call()
}

const refreshUser = async () => {
    try {
        setUser(await getUserAction())
    } catch (e) {
        useError().handleApiError(e)
    }
}

export default () => ({
    error,
    authorized,
    authorize,
    logOut,
    refreshTokenAction,
    refreshUser,
})
